import React, { forwardRef, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Splide, SplideSlide } from '@splidejs/react-splide';

import { PhotographPropTypes } from 'dpl/shared/utils/customPropTypes';
import PhotoAlbumBullets from 'dpl/components/PhotoAlbumBullets';
import PhotoAlbumBulletsV2 from 'dpl/components/PhotoAlbumBullets/PhotoAlbumBulletsV2';
import Icon from 'dpl/common/components/Icon';
import { CAROUSEL_TYPES } from 'dpl/puppy_search_results/utils/constants';

export const DEFAULT_SPLIDE_OPTIONS = {
  arrows: false,
  keyboard: 'focused',
  perPage: 1,
  type: 'loop',
  pagination: false,
  dragMinThreshold: 20,
  snap: true,
  flickMaxPages: 1,
  flickPower: 300
};

const DEFAULT_ARROW_CLASS_NAME = 'black ba b--white-80 bg-white-80 mh2';

const MediaCarouselSplide = forwardRef(
  (
    {
      CarouselMediaComponent,
      mediaFiles,
      smartImageProps,
      muxVideoProps,
      splideOptions,
      showCaptions,
      splideClassName,
      leftArrowClassName,
      rightArrowClassName,
      hasPhotoBullets,
      bulletsContainerClassName,
      bulletStyle,
      maxBulletsPadding,
      roundedCorners,
      analytics
    },
    splideRef
  ) => {
    const fileCount = mediaFiles.length;
    const hasMultipleFiles = fileCount > 1;
    const [bulletIdx, setBulletIdx] = useState(0);
    const [visibleSlides, setVisibleSlides] = useState({ 0: true });

    const ref = useRef(null);
    const muxRef = useRef(null);
    splideRef = splideRef || ref;

    function handleIndexChange() {
      const fileIndex = splideRef.current.splide.index;
      muxRef.current?.pause();
      if (hasPhotoBullets) setBulletIdx(fileIndex);
    }

    function handleVisible(data, { index }) {
      setVisibleSlides(prevStatus => ({ ...prevStatus, [index]: true }));
    }

    function handleHidden(data, { index }) {
      setVisibleSlides(prevStatus => ({ ...prevStatus, [index]: false }));
    }

    function handleNextClick(e) {
      e.preventDefault();
      e.stopPropagation();
      splideRef.current.go('>');
    }

    function handlePreviousClick(e) {
      e.preventDefault();
      e.stopPropagation();
      splideRef.current.go('<');
    }

    const mediaTypes = mediaFiles.map(mf => mf.type ?? 'Image');
    const isLoopingCarousel = splideOptions.type === CAROUSEL_TYPES.LOOP;
    const isPrevButtonVisible = isLoopingCarousel || !visibleSlides[0];
    const isNextButtonVisible =
      isLoopingCarousel || !visibleSlides[fileCount - 1];

    const PhotoAlbumBulletsComponent = !isLoopingCarousel
      ? PhotoAlbumBulletsV2
      : PhotoAlbumBullets;

    return (
      <>
        <Splide
          className={classnames('MediaCarouselSplide', splideClassName)}
          ref={splideRef}
          onMove={handleIndexChange}
          onVisible={handleVisible}
          onHidden={handleHidden}
          options={{
            ...splideOptions,
            drag: hasMultipleFiles,
            waitForTransition: true
          }}
        >
          {mediaFiles.map((mediaFile, idx) => (
            <SplideSlide
              key={idx}
              className={classnames(
                'relative f0 h-100',
                roundedCorners && {
                  'overflow-hidden': true,
                  'br3 br--left': idx === 0,
                  'br3 br--right': idx === fileCount - 1
                }
              )}
            >
              <CarouselMediaComponent
                caption={showCaptions ? mediaFile.caption : null}
                badgeProps={showCaptions ? mediaFile.badge_props : null}
                mediaFile={mediaFile}
                image={mediaFile}
                smartImageProps={smartImageProps}
                muxVideoProps={muxVideoProps}
                analytics={analytics}
                carouselPosition={idx + 1}
                splideRef={splideRef}
                muxRef={
                  idx === splideRef?.current?.splide?.index ? muxRef : undefined
                }
              />
            </SplideSlide>
          ))}
        </Splide>
        {hasMultipleFiles && (
          <>
            <div
              className={classnames(
                'dn db-l transform-center left-0 transition',
                {
                  'o-0': !isPrevButtonVisible
                }
              )}
            >
              <button
                type="button"
                className={classnames(
                  'MediaCarouselSplide__arrow flex items-center justify-center br-100',
                  leftArrowClassName
                )}
                disabled={!isPrevButtonVisible}
                onClick={handlePreviousClick}
              >
                <Icon height="24px" name="fetch-chevron-left" width="24px" />
              </button>
            </div>
            <div
              className={classnames(
                'dn db-l transform-center right-0 transition',
                {
                  'o-0': !isNextButtonVisible
                }
              )}
            >
              <button
                type="button"
                disabled={!isNextButtonVisible}
                className={classnames(
                  'MediaCarouselSplide__arrow flex items-center justify-center br-100',
                  rightArrowClassName
                )}
                onClick={handleNextClick}
                data-test-id="media-carousel-splide-arrow-right"
              >
                <Icon height="24px" name="fetch-chevron-right" width="24px" />
              </button>
            </div>
          </>
        )}
        {hasPhotoBullets && hasMultipleFiles && (
          <div className="absolute bottom-0 right-0 left-0 pb4">
            <PhotoAlbumBulletsComponent
              carouselType={splideOptions.type}
              imageCount={fileCount}
              mediaTypes={mediaTypes}
              currentImageIdx={bulletIdx}
              containerClassName={bulletsContainerClassName}
              maxBulletsPadding={maxBulletsPadding}
              bulletStyle={bulletStyle}
            />
          </div>
        )}
      </>
    );
  }
);

MediaCarouselSplide.displayName = 'MediaCarouselSplide';

MediaCarouselSplide.propTypes = {
  CarouselMediaComponent: PropTypes.func.isRequired,
  mediaFiles: PropTypes.arrayOf(
    PropTypes.shape({
      ...PhotographPropTypes
    })
  ).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  smartImageProps: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  muxVideoProps: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  splideOptions: PropTypes.object.isRequired,
  showCaptions: PropTypes.bool,
  splideClassName: PropTypes.string,
  leftArrowClassName: PropTypes.string,
  rightArrowClassName: PropTypes.string,
  hasPhotoBullets: PropTypes.bool,
  bulletsContainerClassName: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  bulletStyle: PropTypes.object,
  maxBulletsPadding: PropTypes.number,
  analytics: PropTypes.shape({
    position: PropTypes.number,
    view: PropTypes.string
  }),
  roundedCorners: PropTypes.bool
};

MediaCarouselSplide.defaultProps = {
  showCaptions: false,
  splideClassName: '',
  leftArrowClassName: DEFAULT_ARROW_CLASS_NAME,
  rightArrowClassName: DEFAULT_ARROW_CLASS_NAME,
  hasPhotoBullets: false,
  bulletsContainerClassName: 'absolute bottom-0 right-0 left-0 pb4',
  bulletStyle: {},
  maxBulletsPadding: 2,
  analytics: {},
  smartImageProps: {},
  muxVideoProps: {},
  roundedCorners: false
};

export default MediaCarouselSplide;
